import {
  AutocompleteWithLabel,
  IOSSwitch,
  InputWithLabel
} from '@adotel/components'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  useTheme
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import React from 'react'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import PageHeader from '../../components/PageHeader'
import TimePeriodForm from '../../components/TimePeriodForm'
import {
  CURRENCIES_QUERY,
  PAIRED_CURRENCIES_QUERY
} from '../../graphql/currencies'
import {
  EXCHANGE_RATE_LIST_QUERY,
  SAVE_EXCHANGE_RATE_MUTATION
} from '../../graphql/exchangeRate'
import { splitCurrencyPair } from '../../helpers/countriesList'
import { DateObject, addDaysToDate, getTodayDate } from '../../helpers/date'
import { addCommaToPrice } from '../../helpers/numbers'
import { getTranslate } from '../../localization'
import { Currency } from '../../types/currency'
import {
  ExchangeRateData,
  ExchangeRateListFilters
} from '../../types/exchangeRate'
import ExchangeRateAmountRow from './ExchangeRateAmountRow'
import ExchangeRateDateRow from './ExchangeRateDateRow'
import useStyles from './styles/index.Styles'

type RangeType = {
  label: string
  value: 'TWO_WEEKS'
}

const RANGE_ITEMS: RangeType[] = [
  {
    label: getTranslate('View two weeks'),
    value: 'TWO_WEEKS'
  }
]

export default function PriceClassExchangeRate() {
  const classes = useStyles()
  const theme = useTheme()

  const selectedRange = 0
  const [hideZeroes, sethideZeroes] = useState<boolean>(false)
  const [timePickerSelected, setTimePickerSelected] = useState<boolean>(false)
  const [dateObject, setdateObject] = useState<DateObject>({})
  const [
    autoCompleteLabelForPairedCurrency,
    setAutoCompleteLabelForPairedCurrency
  ] = useState<any>()
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    React.useState<boolean>(true)
  const [buyPrice, setBuyPrice] = useState<string>('')
  const [sellPrice, setSellPrice] = useState<string>('')
  const [selectedDateInDatePicker, setselectedDateInDatePicker] =
    useState<DateObject>({
      gregorianStartDate: getTodayDate(),
      gregorianEndDate: addDaysToDate(getTodayDate(), 13)
    })
  const [exchangeRateType, setExchangeRateType] = useState<string>('Buy')
  const [filters, setFilters] = useState<ExchangeRateListFilters>({
    from: selectedDateInDatePicker.gregorianStartDate || '',
    to: selectedDateInDatePicker.gregorianEndDate || '',
    currency: 'IRR',
    buy: true
  })

  const range = RANGE_ITEMS[selectedRange].value

  const datePickerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node) &&
        timePickerSelected
      ) {
        setTimePickerSelected(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerRef, timePickerSelected])

  const { data: currenciesData } = useQuery<{
    currencies: Currency[]
  }>(CURRENCIES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const { data: pairedCurrencies, loading: loadingForPairedCurrencies } =
    useQuery<{
      pairedCurrencies: string[]
    }>(PAIRED_CURRENCIES_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  const [
    getExchangeData,
    { data: exchangeRateList, loading: loadingForExchangeRateList, refetch }
  ] = useLazyQuery<{
    exchangeRateList: ExchangeRateData[]
  }>(EXCHANGE_RATE_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const [saveExchangeRate, { loading: loadingForExchange }] = useMutation(
    SAVE_EXCHANGE_RATE_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        refetch()
        toast.success(data.saveExchangeRate.message)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const onSave = () => {
    saveExchangeRate({
      variables: {
        input: {
          pairedCurrency:
            autoCompleteLabelForPairedCurrency &&
            autoCompleteLabelForPairedCurrency.value,
          buyRate: buyPrice,
          sellRate: sellPrice,
          from: dateObject.gregorianStartDate,
          to: dateObject.gregorianEndDate
        }
      }
    })
  }

  const selectedDayInDatePickerWeekIndex = useMemo(
    () => moment(selectedDateInDatePicker.gregorianStartDate).day(),
    [selectedDateInDatePicker]
  )

  const weekendIndexes = useMemo(
    () => [
      selectedDayInDatePickerWeekIndex <= 4
        ? 4 - selectedDayInDatePickerWeekIndex
        : 11 - selectedDayInDatePickerWeekIndex,
      selectedDayInDatePickerWeekIndex <= 5
        ? 5 - selectedDayInDatePickerWeekIndex
        : 12 - selectedDayInDatePickerWeekIndex
    ],
    [selectedDateInDatePicker]
  )

  const [baseCurrency, quoteCurrency] = splitCurrencyPair(
    autoCompleteLabelForPairedCurrency
      ? autoCompleteLabelForPairedCurrency.value
      : ''
  )

  useEffect(() => {
    if (filters.to) {
      getExchangeData({
        variables: {
          input: filters
        }
      })
    }
  }, [filters])

  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Exchange Rates')} />
      <div className={classes.mainSection}>
        <div className={classes.upSection}>
          <div className={classes.inputs}>
            <AutocompleteWithLabel
              name="PairedCurrencies"
              label={getTranslate('Paired Currencies')}
              placeholder={getTranslate('Select the Paired Currencies')}
              value={autoCompleteLabelForPairedCurrency}
              onChange={(_, v: any) => {
                if (v) {
                  setAutoCompleteLabelForPairedCurrency({
                    value: v.value,
                    label: v.label
                  })
                } else {
                  setAutoCompleteLabelForPairedCurrency(null)
                }
              }}
              getOptionLabel={(option) => option?.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              options={
                pairedCurrencies?.pairedCurrencies.map((item) => ({
                  label: item,
                  value: item
                })) || []
              }
              size="small"
              includeInputInList
              inputSize="small"
              loadingText={getTranslate('Loading') + '...'}
              loading={loadingForPairedCurrencies}
            />
            <InputWithLabel
              type="number"
              label={getTranslate('Buy Amount')}
              placeholder={getTranslate(
                'Please enter the buy amount (higher than the market exchange rate)'
              )}
              helpertext={
                autoCompleteLabelForPairedCurrency
                  ? `${getTranslate('Convert')} ${getTranslate(
                      baseCurrency
                    )} ${getTranslate('To ')} ${getTranslate(
                      quoteCurrency
                    )} (${getTranslate(
                      'higher than the market exchange rate'
                    )}) ${addCommaToPrice(buyPrice)}`
                  : ''
              }
              value={buyPrice}
              inputSize={'small'}
              autoComplete="off"
              onChange={(event) => setBuyPrice(event.currentTarget.value)}
              disabled={!autoCompleteLabelForPairedCurrency}
            />
            <InputWithLabel
              type="number"
              label={getTranslate('Sell Amount')}
              placeholder={getTranslate(
                'Please enter the sell amount (lower than the market exchange rate)'
              )}
              helpertext={
                autoCompleteLabelForPairedCurrency
                  ? `${getTranslate('Convert')} ${getTranslate(
                      quoteCurrency
                    )} ${getTranslate('To ')} ${getTranslate(
                      baseCurrency
                    )} (${getTranslate(
                      'lower than the market exchange rate'
                    )}) ${addCommaToPrice(sellPrice)}`
                  : ''
              }
              value={sellPrice}
              inputSize={'small'}
              autoComplete="off"
              onChange={(event) => setSellPrice(event.currentTarget.value)}
              disabled={!autoCompleteLabelForPairedCurrency}
            />
          </div>
          <div ref={datePickerRef} className={classes.mainTimePeriodForm}>
            <Typography className={classes.dateText}>
              {getTranslate('Please select a date')}
            </Typography>
            <TimePeriodForm
              isOpen={timePickerSelected}
              setTimePickerSelected={setTimePickerSelected}
              dateObject={dateObject}
              onChange={(data: DateObject) => {
                setdateObject(data)
              }}
              onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
              setOnDoneClickForCloseDatePicker={
                setOnDoneClickForCloseDatePicker
              }
              endDateCanBeSelectAsStartDate
            />
          </div>
        </div>
        <div className={classes.MiddleSection}>
          <Button
            onClick={onSave}
            disabled={
              loadingForExchange ||
              !autoCompleteLabelForPairedCurrency ||
              !buyPrice ||
              !sellPrice ||
              !dateObject.gregorianStartDate ||
              !dateObject.gregorianEndDate
            }
            variant="contained"
            color="primary">
            {getTranslate('APPLY')}
            {loadingForExchange && (
              <CircularProgress
                className={classes.circularProgress}
                size={16}
                color="primary"
              />
            )}
          </Button>
        </div>
      </div>
      <div className={classes.midSection}>
        <div className={classes.currencyContainer}>
          <Typography>{getTranslate('Currency')}:</Typography>
          <Select
            value={filters.currency || ''}
            onChange={(event) => {
              setFilters({
                ...filters,
                currency: event.target.value as Currency
              })
            }}>
            {currenciesData?.currencies.map((currency) => (
              <MenuItem
                key={currency}
                value={currency}
                className={classes.menuItemRight}>
                {currency}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className={classes.inventoryTypeSelector}>
          <Typography className={classes.inventoryTypeSelectorLabel}>
            {getTranslate('Exchange Type: ')}
          </Typography>
          <RadioGroup
            className={classes.inventoryTypeRadioGroup}
            value={exchangeRateType}
            onChange={(event) => {
              setExchangeRateType(event.target.value)
              setFilters({
                ...filters,
                buy: event.target.value === 'Buy'
              })
            }}>
            <FormControlLabel
              value="Buy"
              control={<Radio color="primary" />}
              label={getTranslate('Buy')}
            />
            <FormControlLabel
              value="Sell"
              control={<Radio color="primary" />}
              label={getTranslate('Sell')}
            />
          </RadioGroup>
        </div>
        <Button
          startIcon={<RefreshIcon />}
          className={classes.refreshButton}
          onClick={() => refetch()}
          variant="outlined"
          color="primary"
          disabled={loadingForExchangeRateList}>
          {getTranslate('Refresh')}
        </Button>
        <div className={classes.hideZerosFromPriceContainer}>
          <Typography>{getTranslate('Hide 0.000 From Prices')}</Typography>
          <IOSSwitch
            color="primary"
            checked={hideZeroes}
            onChange={(e) => {
              sethideZeroes(e.target.checked)
            }}
          />
        </div>
      </div>
      <div className={classes.downSection}>
        <div className={classes.mainDateRow}>
          <ExchangeRateDateRow
            {...{
              range,
              selectedDateInDatePicker,
              setselectedDateInDatePicker
            }}
          />
        </div>
        {exchangeRateList?.exchangeRateList.map((exchangeRate, index) => (
          <ExchangeRateAmountRow
            key={index}
            data={exchangeRate}
            weekendIndexes={weekendIndexes}
            hideZeroes={hideZeroes}
          />
        ))}
      </div>
      <AdotelSkeleton
        open={loadingForExchangeRateList}
        circlesColor={theme.palette.primary.main}
        numberOfCircles={4}
      />
    </div>
  )
}
